import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Button } from './button'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { X, Loader2 } from 'lucide-react'
import {
  type SubmitPayload,
  type UploadedAttachment,
} from '../Chat/components/RichTextInput/RichTextInput'
import {
  Message,
  FeedbackDialogProps,
  UserInfo,
  useCoachLogicChat,
  useSpeechSynthesis,
  useClipboard,
  MessageList,
  InputArea,
} from './insight-feedback'

function htmlToMarkdown(html: string): string {
  if (!html) return ''

  return html
    .replace(/<(strong|b)>(.*?)<\/(strong|b)>/gi, '**$2**')
    .replace(/<(em|i)>(.*?)<\/(em|i)>/gi, '*$2*')
    .replace(/<(s|strike|del)>(.*?)<\/(s|strike|del)>/gi, '~~$2~~')
    .replace(/<code>(.*?)<\/code>/gi, '`$1`')
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    .replace(/<li>(.*?)<\/li>/gi, '- $1\n')
    .replace(/<\/?ul[^>]*>/gi, '\n')
    .replace(/<\/?ol[^>]*>/gi, '\n')
    .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim()
}

interface RootState {
  user: {
    user: {
      firstName?: string
      lastName?: string
      avatar?: string
    }
  }
}

export function FeedbackDialog({
  isOpen,
  onClose,
  onSubmit,
  feedbackType,
  itemTitle,
  itemDescription,
  itemId,
  existingSessionId,
  onSessionCreated,
  insightContext,
  playContext,
}: FeedbackDialogProps) {
  const [hasProvidedReason, setHasProvidedReason] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [inputResetKey, setInputResetKey] = useState(0)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const user = useSelector((state: RootState) => state.user.user)
  const userInfo: UserInfo = useMemo(
    () => ({
      firstName: user?.firstName,
      lastName: user?.lastName,
      avatar: user?.avatar,
    }),
    [user?.firstName, user?.lastName, user?.avatar]
  )

  // Track previous itemId - initialize with current itemId so we don't reset on initial mount
  // Reset should only happen when switching to a DIFFERENT insight
  const prevItemIdRef = useRef<string | undefined>(itemId)
  const pendingInitialMessagesRef = useRef<{ content: string; role: 'assistant' }[] | null>(null)

  // Generate context-aware system prompt for feedback collection
  const feedbackSystemPrompt = useMemo(() => {
    if (feedbackType === 'play' && playContext) {
      return `You are Coach Logic, a friendly AI assistant collecting feedback about a play suggestion.

CONTEXT - The user is giving feedback about this play:
- Play: "${playContext.playTitle}"
- Description: ${playContext.playDescription || 'Not provided'}
- Why it matters: ${playContext.whyItMatters || 'Not provided'}
- Related insight: ${playContext.insightTitle || 'Not specified'}

YOUR GOAL: Understand why this play didn't resonate with the user so future suggestions can be improved.

CRITICAL RULES:
- Your ONLY job is to collect feedback - do NOT give advice, suggestions, or explanations
- Keep responses SHORT (1-3 sentences max)
- Be conversational, warm, and appreciative
- Ask ONE follow-up question at a time to understand their concerns better
- When user gives vague feedback (like "not useful", "random", "not relevant"), ask them to elaborate on WHY it feels that way
- Thank users for their input and show genuine interest in understanding their perspective
- Never be defensive about the suggestion
- Never give bullet points or lists
- Do NOT ask what they'd want instead - focus on understanding why this specific suggestion didn't work

RESPONSE STYLE EXAMPLES:
- If user says "This is random": "I appreciate you telling me that! Could you help me understand what felt random about it? Was it the timing, or does it not connect to what you're currently working on?"
- If user says "Not useful": "Thanks for the feedback! I'd love to understand better - what about this play doesn't fit your current situation?"
- If user says "Already doing this": "Good to know you're already on top of this! Is there anything about how we presented it that could have been clearer?"`
    }

    if (feedbackType === 'insight') {
      const playsInfo = insightContext?.plays?.map((p) => p.title).join(', ') || 'None'
      const goalsInfo = insightContext?.goals?.map((g) => g.title).join(', ') || 'None'

      return `You are Coach Logic, a friendly AI assistant collecting feedback about a business insight.

CONTEXT - The user is giving feedback about this insight:
- Insight: "${itemTitle}"
- Description: ${itemDescription || 'Not provided'}
- Why it matters: ${insightContext?.why || 'Not provided'}
- Priority: ${insightContext?.priority || 'Not set'}
- Related plays: ${playsInfo}
- Related goals: ${goalsInfo}

YOUR GOAL: Understand why this insight didn't resonate with the user so future insights can be more relevant.

CRITICAL RULES:
- Your ONLY job is to collect feedback - do NOT give advice, suggestions, or explanations
- Keep responses SHORT (1-3 sentences max)
- Be conversational, warm, and appreciative
- Ask ONE follow-up question at a time to understand their concerns better
- When user gives vague feedback (like "not useful", "random", "not relevant"), ask them to elaborate on WHY it feels that way
- Thank users for their input and show genuine interest in understanding their perspective
- Never be defensive about the insight
- Never give bullet points or lists
- Do NOT ask what they'd want instead - focus on understanding why this specific insight didn't work

RESPONSE STYLE EXAMPLES:
- If user says "This is random": "Thanks for letting me know! Could you share what felt off about this insight? Is it not relevant to your current priorities, or does the data seem inaccurate?"
- If user says "Not useful": "I appreciate the feedback! Could you help me understand what didn't click? Was it the information itself, or how it was presented?"
- If user says "Already know this": "Got it! Was this something you've been tracking, or is it common knowledge in your industry? That helps me understand the gap."`
    }

    // Default fallback prompt
    return `You are Coach Logic, a friendly AI assistant collecting feedback.

CONTEXT: The user is providing feedback about "${itemTitle}" (${feedbackType}).

YOUR GOAL: Understand why this didn't work for the user to improve future suggestions.

CRITICAL RULES:
- Your ONLY job is to collect feedback - do NOT give advice or explanations
- Keep responses SHORT (1-3 sentences max)
- Be conversational, warm, and appreciative
- Ask ONE follow-up question at a time to understand WHY
- When user gives vague feedback, ask them to elaborate
- Thank users for their input
- Never give bullet points or lists`
  }, [feedbackType, itemTitle, itemDescription, insightContext, playContext])

  const {
    messages,
    setMessages,
    sessionId,
    isTyping,
    isLoadingHistory,
    sendMessage,
    addUserMessage,
    initialize,
    reset,
    hasUserMessage,
  } = useCoachLogicChat(itemId, existingSessionId)

  const { isSpeaking, speak, cancel: cancelSpeech } = useSpeechSynthesis()
  const { copiedId, copy } = useClipboard()

  // Only reset when itemId actually CHANGES to a different value
  // This preserves cache when reopening the same insight
  useEffect(() => {
    if (prevItemIdRef.current !== undefined && prevItemIdRef.current !== itemId) {
      reset()
      setHasProvidedReason(false)
    }
    prevItemIdRef.current = itemId
  }, [itemId, reset])

  const uploadAdapter = async (file: File): Promise<UploadedAttachment> => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      const mockUrl = URL.createObjectURL(file)
      return {
        type: file.type?.startsWith('image/') ? 'image' : 'file',
        url: mockUrl,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }
    } catch (error) {
      console.error('Upload failed:', error)
      throw new Error(`Failed to upload ${file.name}`)
    }
  }

  const handleVoiceMessage = async (
    audioBlob: Blob,
    transcript?: string,
    durationSeconds?: number
  ) => {
    try {
      const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
        type: 'audio/webm;codecs=opus',
      })

      const uploadedAudio = await uploadAdapter(audioFile)

      addUserMessage(transcript || 'Voice message', {
        htmlContent: transcript ? `<p>${transcript}</p>` : '<p>Voice message</p>',
        attachments: [uploadedAudio],
        isVoiceMessage: true,
        voiceTranscript: transcript,
        voiceDuration: durationSeconds,
      })

      if (!hasProvidedReason) setHasProvidedReason(true)

      if (transcript && transcript.trim()) {
        // Pass system prompt only if this is a fresh conversation (no existing session)
        const promptToSend = !sessionId ? feedbackSystemPrompt : undefined
        await sendMessage(transcript, sessionId, onSessionCreated, undefined, promptToSend)
      } else if (!transcript) {
        addUserMessage('(Voice message - transcription unavailable)', {
          htmlContent: '<p><em>Voice message recorded but transcription failed.</em></p>',
        })
      }

      setIsRecording(false)
    } catch (error) {
      console.error('Error processing voice message:', error)
      addUserMessage('(Voice message error)', {
        htmlContent: '<p><em>Failed to process voice message.</em></p>',
      })
      setIsRecording(false)
    }
  }

  const handleRichTextSubmit = async (payload: SubmitPayload) => {
    if (!payload.text.trim() && (!payload.attachments || payload.attachments.length === 0)) return
    if (isTyping) return

    const markdownContent = htmlToMarkdown(payload.html)

    addUserMessage(markdownContent, { attachments: payload.attachments })
    setInputResetKey((prev) => prev + 1)

    if (!hasProvidedReason) setHasProvidedReason(true)

    const initialMessages = pendingInitialMessagesRef.current
    pendingInitialMessagesRef.current = null

    // Pass system prompt only if this is a fresh conversation (no existing session)
    const promptToSend = !sessionId ? feedbackSystemPrompt : undefined
    await sendMessage(
      markdownContent,
      sessionId,
      onSessionCreated,
      initialMessages || undefined,
      promptToSend
    )
  }

  const handleConfirmBlock = useCallback(() => {
    // Close dialog immediately for better UX
    onClose()

    // Submit feedback in background (don't await)
    const userMessage = messages.find((m) => m.sender === 'user')
    let feedback = userMessage?.content || 'No reason provided'

    if (userMessage?.isVoiceMessage && userMessage?.voiceTranscript) {
      feedback = userMessage.voiceTranscript
      if (userMessage.voiceDuration) {
        feedback += ` [Voice message: ${Math.round(userMessage.voiceDuration)}s]`
      }
    } else if (userMessage?.attachments && userMessage.attachments.length > 0) {
      const attachmentInfo = userMessage.attachments.map((att) => att.fileName).join(', ')
      feedback += ` [Attachments: ${attachmentInfo}]`
    }

    onSubmit(feedback)
  }, [messages, onClose, onSubmit])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  const welcomeMessage: Message = useMemo(() => {
    const content =
      feedbackType === 'play'
        ? `I noticed this play wasn't quite right for you. I'd love to understand why so I can suggest better plays in the future. What's your main concern with "**${itemTitle}**"?`
        : `I noticed this insight wasn't helpful for you. I'd love to understand why so I can provide better insights in the future. What's your main concern with "**${itemTitle}**"?`

    return { id: 'welcome', content, sender: 'coach', timestamp: new Date(1) }
  }, [feedbackType, itemTitle])

  const greetingMessage: Message = useMemo(
    () => ({
      id: 'greeting',
      content: `Hi ${userInfo.firstName || 'there'}! 👋`,
      sender: 'coach',
      timestamp: new Date(0),
    }),
    [userInfo.firstName]
  )

  useEffect(() => {
    if (isOpen && itemId) {
      const { hasExisting } = initialize(existingSessionId)

      if (!hasExisting) {
        setHasProvidedReason(false)
        const now = new Date()
        const greetingWithTime: Message = { ...greetingMessage, timestamp: now }
        const welcomeWithTime: Message = {
          ...welcomeMessage,
          timestamp: new Date(now.getTime() + 10),
        }

        setMessages([greetingWithTime, welcomeWithTime])
        pendingInitialMessagesRef.current = [
          { content: greetingMessage.content, role: 'assistant' },
          { content: welcomeMessage.content, role: 'assistant' },
        ]
      } else {
        // Clear pending initial messages if session already exists
        pendingInitialMessagesRef.current = null
      }
    } else if (!isOpen) {
      cancelSpeech()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId, existingSessionId])

  useEffect(() => {
    if (hasUserMessage) setHasProvidedReason(true)
  }, [hasUserMessage])

  // Track if we've already handled the empty history case to prevent duplicate runs
  const hasHandledEmptyHistoryRef = useRef(false)

  // Reset the flag when sessionId changes (new conversation)
  useEffect(() => {
    hasHandledEmptyHistoryRef.current = false
  }, [sessionId])

  // Show greeting messages if history loaded but returned empty (stream failed or no messages)
  useEffect(() => {
    // Only run once per session, and only when:
    // 1. Not loading history anymore
    // 2. We have a sessionId (meaning we tried to fetch history)
    // 3. Messages are empty
    if (
      !isLoadingHistory &&
      sessionId &&
      messages.length === 0 &&
      !hasHandledEmptyHistoryRef.current
    ) {
      hasHandledEmptyHistoryRef.current = true
      console.log('History loaded but empty, showing greeting messages')
      const now = new Date()
      const greetingWithTime: Message = { ...greetingMessage, timestamp: now }
      const welcomeWithTime: Message = {
        ...welcomeMessage,
        timestamp: new Date(now.getTime() + 10),
      }
      setMessages([greetingWithTime, welcomeWithTime])
      pendingInitialMessagesRef.current = [
        { content: greetingMessage.content, role: 'assistant' },
        { content: welcomeMessage.content, role: 'assistant' },
      ]
    }
  }, [isLoadingHistory, sessionId, messages.length, greetingMessage, welcomeMessage, setMessages])

  // Track previous message count to only auto-scroll on new messages or typing indicator
  const prevMessageCountRef = useRef(0)
  const prevIsTypingRef = useRef(false)

  useEffect(() => {
    // Auto-scroll if message count increased OR typing indicator appeared
    const messagesIncreased = messages.length > prevMessageCountRef.current
    const typingStarted = isTyping && !prevIsTypingRef.current

    if (scrollAreaRef.current && (messagesIncreased || typingStarted)) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
    prevMessageCountRef.current = messages.length
    prevIsTypingRef.current = isTyping
  }, [messages.length, isTyping])

  if (!isOpen) return null

  const dialogTitle = feedbackType === 'play' ? 'Play Feedback' : 'Share Your Feedback'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-dialog-title"
      className="fixed inset-0 bg-app-black-50 flex items-center justify-center z-[99999] pt-20 pb-5"
    >
      <div
        ref={dialogRef}
        className="bg-app-white-100 rounded-xl max-w-[600px] w-[90%] max-h-[700px] shadow-xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-app-grey-30">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
              <AvatarFallback className="bg-app-primary-60 text-app-white-100">CL</AvatarFallback>
            </Avatar>
            <div>
              <h2 id="feedback-dialog-title" className="text-lg font-semibold m-0">
                {dialogTitle}
              </h2>
              <p className="text-sm text-app-grey-50 m-0">Coach Logic</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleConfirmBlock}
            aria-label="Close feedback dialog"
            className="h-10 w-10 p-0 bg-transparent hover:bg-app-grey-20 rounded-full"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Messages Area */}
        <div ref={scrollAreaRef} className="flex-1 p-6 max-h-[400px] overflow-y-auto">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="ml-2 text-app-grey-50">Loading conversation...</span>
            </div>
          ) : (
            <MessageList
              messages={messages}
              isTyping={isTyping}
              isSpeaking={isSpeaking}
              copiedMessageId={copiedId}
              onSpeak={speak}
              onCopy={copy}
              userInfo={userInfo}
            />
          )}
        </div>

        {/* Input Area */}
        <InputArea
          isRecording={isRecording}
          isTyping={isTyping}
          resetKey={inputResetKey}
          onSubmit={handleRichTextSubmit}
          onStartRecording={() => setIsRecording(true)}
          onVoiceMessage={handleVoiceMessage}
          onVoiceCancel={() => setIsRecording(false)}
          onEndConversation={handleConfirmBlock}
          uploadAdapter={uploadAdapter}
        />
      </div>
    </div>
  )
}
